import Grid from '@mui/material/Grid';
import { usePopupState } from 'material-ui-popup-state/hooks';
import { ReactNode } from 'react';

import Searcher from 'src/common/components/Searcher';
import ViewSwitch from 'src/common/components/ViewSwitch';
import FilterPopoverButton from 'src/common/components/list-filters/FilterPopoverButton';
import FiltersPopover from 'src/common/components/list-filters/FiltersPopover';
import FiltersPopoverSearchSort from 'src/common/components/list-filters/FiltersPopoverSearchSort';
import {
  listFilterActionsColumnSize,
  listFilterColumnSpacing,
  listFilterCompactRowSx,
  listFilterGridContainerSx,
  listFilterLeadingColumnSize,
  listFilterRowSpacing,
  listFilterSearchColumnSize,
  listFilterSingleRowSx,
  listFilterToolbarClusterSx,
  listFilterToolbarEndColumnSize,
  listFilterToolbarEndSx,
  listFilterToolbarSearchSx,
} from 'src/common/constants/listFilterGrid';
import { hasActiveListFilter } from 'src/helpers/listFilters';
import { EOrder } from 'src/interfaces/Order';
import { EView } from 'src/interfaces/View';

interface Props {
  filterPopoverContent?: ReactNode;
  /** Rendered under Search in the filter popover (e.g. user Autocomplete). */
  filterPopoverBelowSearch?: ReactNode;
  filterPopoverWidth?: number;
  hasActiveFilters?: boolean;
  leading?: ReactNode;
  onSearchChange: (query: string) => void;
  onSetView: (view: EView) => void;
  order: EOrder;
  orderByKey: string;
  popupId?: string;
  searchPlaceholder?: string;
  searchQuery: string;
  /** Where the search field is rendered. Default `'popover'`. */
  searchPlacement?: 'popover' | 'toolbar';
  setOrder: (order: EOrder) => void;
  setOrderByKey: (key: string) => void;
  showFilters?: boolean;
  sortLabel?: string;
  sortOptions: { label: string; value: string }[];
  toolbarActions?: ReactNode;
  trailing?: ReactNode;
  view: EView;
  /** Shrink-wrapped row for inline header slots (e.g. media modal). */
  compact?: boolean;
  /** Forwarded to the filter button for guided tour targeting. */
  filterButtonDataTour?: string;
  /** Forwarded to the filter popover paper for guided tour highlighting. */
  filterPopoverDataTour?: string;
}

const ListFilterToolbar = ({
  filterPopoverContent,
  filterPopoverBelowSearch,
  filterPopoverWidth,
  hasActiveFilters = false,
  leading,
  onSearchChange,
  onSetView,
  order,
  orderByKey,
  popupId = 'listFilters',
  searchPlaceholder,
  searchQuery,
  searchPlacement = 'popover',
  setOrder,
  setOrderByKey,
  showFilters = true,
  sortLabel,
  sortOptions,
  toolbarActions,
  trailing,
  view,
  compact = false,
  filterButtonDataTour,
  filterPopoverDataTour,
}: Props) => {
  const filterMenu = usePopupState({ variant: 'popover', popupId });

  const searchInToolbar = searchPlacement === 'toolbar';
  const popoverActive = searchInToolbar
    ? hasActiveFilters
    : hasActiveFilters || hasActiveListFilter(searchQuery);

  return (
    <Grid
      container
      columnSpacing={listFilterColumnSpacing}
      rowSpacing={compact ? 0 : listFilterRowSpacing}
      sx={[listFilterGridContainerSx, compact ? listFilterCompactRowSx : listFilterSingleRowSx]}
    >
      {leading ? (
        <Grid key="leading" size={listFilterLeadingColumnSize} sx={listFilterToolbarClusterSx}>
          {leading}
        </Grid>
      ) : null}
      {toolbarActions ? (
        <Grid key="toolbarActions" size={listFilterActionsColumnSize} sx={listFilterToolbarClusterSx}>
          {toolbarActions}
        </Grid>
      ) : null}
      {searchInToolbar ? (
        <Grid key="search" size={listFilterSearchColumnSize} sx={listFilterToolbarSearchSx}>
          <Searcher
            expanded
            onChange={onSearchChange}
            query={searchQuery}
            width="100%"
            openWidth="100%"
            placeholder={searchPlaceholder}
          />
        </Grid>
      ) : null}
      <Grid key="end" size={listFilterToolbarEndColumnSize} sx={listFilterToolbarEndSx}>
        {showFilters ? (
          <>
            <FilterPopoverButton popupState={filterMenu} active={popoverActive} data-tour={filterButtonDataTour} />
            <FiltersPopover popupState={filterMenu} width={filterPopoverWidth} data-tour={filterPopoverDataTour}>
              <FiltersPopoverSearchSort
                searchQuery={searchQuery}
                onSearchChange={onSearchChange}
                searchPlaceholder={searchPlaceholder}
                showSearch={!searchInToolbar}
                belowSearch={filterPopoverBelowSearch}
                order={order}
                orderByKey={orderByKey}
                setOrder={setOrder}
                setOrderByKey={setOrderByKey}
                sortLabel={sortLabel}
                sortOptions={sortOptions}
              >
                {filterPopoverContent}
              </FiltersPopoverSearchSort>
            </FiltersPopover>
          </>
        ) : null}
        {trailing}
        <ViewSwitch view={view} onSetView={onSetView} />
      </Grid>
    </Grid>
  );
};

export default ListFilterToolbar;
